import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchModels } from '../hooks'
import { SearchResultCard } from './SearchResultCard'
import { SkeletonCard, EmptyState } from '@/shared/components'
import { IconSearch } from '@/app/components/icons'

interface SelectedItem {
  readonly provider: string
  readonly externalId: string
}

interface SearchResultGridProps {
  readonly query: string
  readonly provider: string | undefined
  readonly resourceType: string | undefined
  readonly onSelectItem: (item: SelectedItem) => void
}

const PAGE_SIZE = 20

export function SearchResultGrid({ query, provider, resourceType, onSelectItem }: SearchResultGridProps) {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)

  // Reset to page 1 when search parameters change
  useEffect(() => {
    setPage(1)
  }, [query, provider, resourceType])

  const { data, isLoading, isError } = useSearchModels(query, provider, resourceType, page, PAGE_SIZE)
  const results = data?.results ?? []
  const allItems = results.flatMap((r) => r.items)

  if (query.length < 2) {
    return null
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonCard key={i} variant="wide" />
        ))}
      </div>
    )
  }

  if (isError) {
    return (
      <div className="py-12 text-center">
        <span className="text-sm text-tg-destructive">
          {t('search.error')}
        </span>
      </div>
    )
  }

  if (allItems.length === 0) {
    return (
      <EmptyState
        icon={<IconSearch className="h-8 w-8" />}
        title={t('search.noResults')}
        description={t('search.noResultsDesc')}
      />
    )
  }

  // Group by provider if multiple providers returned results
  const providerGroups = results.filter((r) => r.items.length > 0)
  const hasMultipleProviders = providerGroups.length > 1

  return (
    <div className="flex flex-col gap-4">
      {hasMultipleProviders ? (
        providerGroups.map((group) => (
          <div key={group.provider} className="flex flex-col gap-3">
            {/* Provider group header */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-tg-hint">
                {t('search.resultsFrom')} {group.provider}
              </span>
              <div
                className="h-px flex-1 bg-tg-secondary"
              />
              <span className="text-xs text-tg-hint">
                {group.total_count}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {group.items.map((item) => (
                <SearchResultCard
                  key={`${item.provider}:${item.external_id}`}
                  item={item}
                  onClick={() => onSelectItem({ provider: item.provider, externalId: item.external_id })}
                />
              ))}
            </div>
          </div>
        ))
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {allItems.map((item) => (
            <SearchResultCard
              key={`${item.provider}:${item.external_id}`}
              item={item}
              onClick={() => onSelectItem({ provider: item.provider, externalId: item.external_id })}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {(page > 1 || results.some((r) => r.has_more)) && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded-lg border border-tg-hint/10 bg-tg-secondary px-3 py-1.5 text-sm font-medium text-tg-text transition-all duration-150 hover:border-tg-hint/20 active:scale-[0.97] disabled:opacity-40"
          >
            {t('common.previous')}
          </button>
          <span className="text-sm text-tg-hint">
            {page}
          </span>
          <button
            type="button"
            disabled={!results.some((r) => r.has_more)}
            onClick={() => setPage((p) => p + 1)}
            className="rounded-lg border border-tg-hint/10 bg-tg-secondary px-3 py-1.5 text-sm font-medium text-tg-text transition-all duration-150 hover:border-tg-hint/20 active:scale-[0.97] disabled:opacity-40"
          >
            {t('common.next')}
          </button>
        </div>
      )}
    </div>
  )
}
